import { memo, useEffect, useRef, useState } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, NodeResizer, Position } from '@xyflow/react'
import { DEFAULT_FRAME_COLOR, FRAME_COLORS, FRAME_RATIOS, isDarkColor } from './frames'

/**
 * A frame: a named area that holds pictures (src/web/canvas/frames.ts).
 *
 * Drawn beneath everything, so the pictures in it stay on top and take their
 * own clicks. A press anywhere else on it selects it and drags it, pictures
 * and all. Grey by default with a dark edge, like a sheet laid on the board.
 *
 * Resized freely; with Shift held, in proportion -- the same key as in Miro
 * and every drawing program. That is done by the board, on the resize itself
 * (ProjectCanvas, lockFrameRatio), so Shift can be pressed or let go in the
 * middle of a drag. Selected on its own, it shows a small bar above
 * it to set its shape and its colour. The colour picked becomes this person's
 * colour for new frames; the shape does not, and a new frame is always 16:9
 * (Huanrui, 2026-09-30).
 *
 * Its ports are for skills that take a set of pictures in no order. None of
 * today's skills do, so nothing connects to them yet; the board refuses the
 * wire rather than the frame hiding its ports and growing them later.
 */

export interface FrameNodeData extends Record<string, unknown> {
  name?: string
  /** Its colour. Saved with the board; a frame from before colours is grey. */
  color?: string
  /** How many pictures are in it. Handed in by the board, never stored. */
  count?: number
  /** Selected and nothing else is: the bar is shown. Handed in, never stored. */
  solo?: boolean
  /** Handed in by the board, never stored. */
  onRename?: (name: string) => void
  onRatio?: (ratio: number) => void
  onColor?: (color: string) => void
  /** The eyedropper: the board takes over until a colour is clicked (ColourPicker.tsx). */
  onPick?: () => void
}

export const FrameNode = memo(function FrameNode({ data, selected, width, height }: NodeProps) {
  const frame = data as FrameNodeData
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const field = useRef<HTMLInputElement>(null)
  const color = frame.color || DEFAULT_FRAME_COLOR
  const dark = isDarkColor(color)
  const ratio = width && height ? width / height : 0

  useEffect(() => {
    if (editing) field.current?.select()
  }, [editing])

  const finish = (keep: boolean) => {
    setEditing(false)
    const name = draft.trim()
    if (keep && name && name !== frame.name) frame.onRename?.(name)
  }

  return (
    <div
      className={`cframe${selected ? ' cframe--selected' : ''}${dark ? ' cframe--dark' : ''}`}
      style={{ background: color }}
    >
      <NodeResizer
        isVisible={selected}
        minWidth={160}
        minHeight={90}
        lineClassName="cnode__resize-line"
        handleClassName="cnode__resize-handle"
      />

      {/* Part of the frame itself, just above its top-right corner, so it
          grows and shrinks with the frame as the board is zoomed and stays in
          the same place relative to it (Huanrui, 2026-09-30). React Flow's
          own toolbar is drawn at screen size, which is what it was before. */}
      {selected && frame.solo ? (
        <div className="cframe__bar nodrag" role="toolbar" aria-label="Frame">
          {FRAME_RATIOS.map((one) => (
            <button
              key={one.label}
              type="button"
              className={`cframe__ratio${ratio && Math.abs(ratio - one.value) < 0.02 ? ' cframe__ratio--on' : ''}`}
              onClick={() => frame.onRatio?.(one.value)}
              title={`Make it ${one.label}`}
            >
              {one.label}
            </button>
          ))}
          <span className="cframe__rule" aria-hidden="true" />
          {FRAME_COLORS.map((one) => (
            <button
              key={one.value}
              type="button"
              className={`cframe__swatch${one.value === color ? ' cframe__swatch--on' : ''}`}
              style={{ background: one.value }}
              onClick={() => frame.onColor?.(one.value)}
              title={one.name}
              aria-label={one.name}
              aria-pressed={one.value === color}
            />
          ))}
          {/* The eyedropper (Huanrui, 2026-10-06): any colour shown on the
              board -- a picture, an Edit area window, another frame -- becomes
              this frame's (ColourPicker.tsx). A colour picked this way is this
              frame's only; new frames keep the swatch last chosen. */}
          {frame.onPick ? (
            <button
              type="button"
              className={`cframe__pick${FRAME_COLORS.some((one) => one.value === color) ? '' : ' cframe__pick--on'}`}
              // The picked colour fills it, and the dropper stays drawn on top
              // in whichever of black or white reads on that colour.
              style={
                FRAME_COLORS.some((one) => one.value === color)
                  ? undefined
                  : { background: color, color: dark ? '#ffffff' : '#111111' }
              }
              title="Pick a colour from anything on the board"
              aria-label="Pick a colour from the board"
              onClick={() => frame.onPick?.()}
            >
              <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                <path
                  d="M10.6 2.2a1.9 1.9 0 0 1 2.7 2.7l-1.5 1.5.7.7-1 1-.7-.7-5.1 5.1H3.6v-2.1l5.1-5.1-.7-.7 1-1 .7.7z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : null}
        </div>
      ) : null}

      <Handle type="target" id="in" position={Position.Left} className="cnode__port" />
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      {editing ? (
        <input
          ref={field}
          className="cframe__label cframe__label--edit nodrag"
          value={draft}
          maxLength={80}
          aria-label="Frame name"
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => finish(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') finish(true)
            if (event.key === 'Escape') finish(false)
          }}
        />
      ) : (
        <span
          className="cframe__label"
          title="Double-click to rename"
          onDoubleClick={() => {
            setDraft(frame.name ?? '')
            setEditing(true)
          }}
        >
          {frame.name || 'Frame'}
          {frame.count ? <span className="cframe__count">{frame.count}</span> : null}
        </span>
      )}
    </div>
  )
})
